<script setup lang="ts">
import {
  autoPlacement,
  autoUpdate,
  offset,
  useFloating,
  type Placement,
  type Strategy,
} from "@floating-ui/vue";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

import SimpleButton, { type ButtonProps } from "@/components/ui/SimpleButton.vue";

interface DropdownMenuProps extends ButtonProps {
  allowedPlacements?: Placement[];
  strategy?: Strategy;
  highlightWhenOpen?: boolean;
  helpLabel?: string;
}

defineOptions({
  inheritAttrs: false,
});

const {
  label = null,
  iconLeft = null,
  iconRight = null,
  kind = "tertiary",
  allowedPlacements = ["top-end", "bottom-end", "top-start", "bottom-start"],
  strategy = "fixed",
  highlightWhenOpen = false,
  helpLabel = "",
} = defineProps<DropdownMenuProps>();
const emit = defineEmits(["open", "close"]);

const isOpen = ref(false);
const el = ref<HTMLDivElement>();
const reference = ref<HTMLElement>();
const floating = ref<HTMLDivElement>();
const { floatingStyles } = useFloating(reference, floating, {
  strategy,
  middleware: [autoPlacement({ allowedPlacements: allowedPlacements as Placement[] }), offset(5)],
  whileElementsMounted: autoUpdate,
});

function onClick(e: Event) {
  if (el.value && floating.value) {
    // is it a click outside or a click on a final item ?
    const t = e.target as HTMLElement;
    const isTargetInside = el.value.contains(t);
    const isTargetFinal = t.closest('[data-is-final="true"]') !== null;

    if (!isTargetInside || isTargetFinal) {
      close();
    }
  }
}

// Mouse move listener to close the dropdown when the mouse moves
function onMouseMove() {
  if (
    el.value &&
    !el.value.checkVisibility({
      opacityProperty: true,
      contentVisibilityAuto: true,
      visibilityProperty: true,
    })
  ) {
    close();
  }
}

function toggle() {
  if (isOpen.value) {
    close();
  } else {
    open();
  }
}

function open() {
  isOpen.value = true;
  emit("open");
}

function close() {
  isOpen.value = false;
  emit("close");
}

// Intersection observer to close the dropdown when it is not visible
const intersectionObserver = new IntersectionObserver(
  ([entry]) => {
    if (!entry.isIntersecting) {
      close();
    }
  },
  {
    root: document.body,
    threshold: 0,
  }
);

const transitionName = computed(() => {
  const isVisible = el.value?.checkVisibility({
    opacityProperty: true,
    contentVisibilityAuto: true,
    visibilityProperty: true,
  });
  return isVisible ? "fade" : "";
});

onMounted(() => {
  document.addEventListener("click", onClick);
  document.addEventListener("mousemove", onMouseMove);
  if (el.value) {
    intersectionObserver.observe(el.value);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onClick);
  document.removeEventListener("mousemove", onMouseMove);
  intersectionObserver.disconnect();
});

defineExpose({
  toggle,
  open,
  close,
});
</script>

<template>
  <div class="dropdown" :class="{ 'open-highlight': isOpen && highlightWhenOpen }" ref="el">
    <SimpleButton
      class="main-button"
      :kind="kind"
      :label="label!"
      :icon-left="iconLeft!"
      :icon-right="iconRight!"
      @click="toggle()"
      ref="reference"
      v-bind="$attrs"
    >
      <template v-if="helpLabel.length > 0" v-slot:labelPrefix>
        <span class="help-label">
          {{ helpLabel }}
        </span>
      </template>
    </SimpleButton>
    <Transition :name="transitionName">
      <div class="items" v-if="isOpen" ref="floating" :style="floatingStyles">
        <slot></slot>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.dropdown {
  position: relative;
  display: inline-block;

  > .main-button {
    transition:
      border-color var(--animation-duration) var(--animation-easing),
      box-sahdow var(--animation-duration) var(--animation-easing);
  }

  &.open-highlight > .main-button.tertiary {
    border-color: var(--color-button-background-primary);
    box-shadow: 0 0 0 4px rgb(from var(--color-button-background-primary) r g b / 12%);
  }

  .items {
    position: fixed;
    z-index: 9999;
    display: flex;
    overflow: visible;
    width: max-content;
    flex-direction: column;
    border: solid var(--stroke-width-m) var(--color-stroke-background-primary);
    border-radius: var(--radius-xs);
    background-color: var(--color-background-primary);
    box-shadow: var(--box-shadow-m);
  }

  .help-label {
    color: var(--color-text-secondary);
  }
}
</style>
